import React from 'react';
import FiltersBar from '../../../Table/FiltersBar';
import {
    useSupplementsComplementaryData,
    useSupplementsList,
} from '../../contextsProperty/ContextSupplements';
import Button from '../../../muiComponents/Button';
import useTranslate from '../../../customHooks/useTranslate';
import ChangeViewButtonBar from '../../../Table/ChangeViewButtonBar';
import Grid from '../../../muiComponents/Grid';
import GridChild from '../../../muiComponents/GridChild';
import { SupplementListViewType } from '../../../types/supplement/supplementsTypes';
import {
    filterDoubleColumnGrid,
    filterSingleColumnGrid,
} from '../../../utils/supplements/supplementUtils';
import TextInput from '../../../muiComponents/TextInput/TextInput';
import LoadedOptionsTypeahead from '../../../muiComponents/LoadedOptionsTypeahead';
import { IFilterOptionState } from '../../../types/lists/listTypes';
import DateInput from '../../../muiComponents/DateInput';
import { useODataMultipleQueries } from '../../../customHooks/useODataMultipleQueries';
import { readOnlyEntities } from '../../../utils/commonVariables/entityKeys';
import { commonIcons } from '../../../utils/commonVariables/commonIcons';
import { useSearchParams } from '../../../customHooks/useSearchParams';
import {
    gridSpacing10,
    gridSpacing15,
} from '../../../utils/commonVariables/grid';
import useUserProfile from '../../../customHooks/useUserProfile';
import Tooltip from '../../../muiComponents/Tooltip';

const SupplementsFiltersBar = () => {
    const {
        handleFilterChange,
        filters,
        handleSearch,
        resetFilters,
        isLoading,
        currentResults,
        totalResults,
        visibleView,
        changeVisibleView,
    } = useSupplementsList();
    const { supplierHotels } = useSupplementsComplementaryData();

    const { readOnlyUser } = useUserProfile();

    const supplierHotelSelected = filters.IdSupplierHotel
        .value as IFilterOptionState;
    const IdSupplierHotel = supplierHotelSelected?.Id
        ? parseInt(supplierHotelSelected?.Id)
        : null;

    const supplierHotelDependentQueries = [
        {
            key: 'supplierHotelRoomTypes',
            endpoint: {
                entityName: readOnlyEntities.SupplierHotelRoomType,
                select: ['Id', 'Name', 'IdSupplierHotel'],
                orderBy: 'Name asc',
                filter: [{ IdSupplierHotel }],
            },
        },
        {
            key: 'supplierHotelMealPlans',
            endpoint: {
                entityName: readOnlyEntities.SupplierHotelMealPlan,
                select: ['Id', 'Name', 'IdSupplierHotel'],
                orderBy: 'Name asc',
                filter: [{ IdSupplierHotel }],
            },
        },
        {
            key: 'supplierHotelRatePlans',
            endpoint: {
                entityName: readOnlyEntities.SupplierHotelRatePlan,
                select: ['Id', 'Name', 'IdSupplierHotel'],
                orderBy: 'Name asc',
                filter: [{ IdSupplierHotel }],
            },
        },
    ];

    const {
        supplierHotelRoomTypes,
        supplierHotelMealPlans,
        supplierHotelRatePlans,
    } = useODataMultipleQueries({
        queries: supplierHotelDependentQueries,
        dependencies: [IdSupplierHotel],
        condition: !!supplierHotelSelected,
    });

    const translate = useTranslate();

    const { setSearchParam } = useSearchParams({ base64: true });

    const handleAddSupplement = () => {
        setSearchParam('id', 'new');
    };

    const buttonsButtonBar = [
        {
            icon:
                visibleView === SupplementListViewType.CALENDAR
                    ? commonIcons.LIST
                    : commonIcons.CALENDAR,
            label:
                visibleView === SupplementListViewType.CALENDAR
                    ? 'list'
                    : 'calendar',
            view:
                visibleView === SupplementListViewType.CALENDAR
                    ? SupplementListViewType.TABLE
                    : SupplementListViewType.CALENDAR,
        },
    ];

    return (
        <FiltersBar
            title='supplements'
            clearFilters={resetFilters}
            search={async () => await handleSearch(1)}
            isLoading={isLoading}
            currentResults={currentResults}
            totalResults={totalResults}
            startButtonBar={
                <Tooltip
                    titleText='read_only_supplements'
                    hideTooltip={!readOnlyUser}
                >
                    <span>
                        <Button
                            variant='contained'
                            startIcon='Add'
                            onClick={handleAddSupplement}
                            disabled={readOnlyUser}
                        >
                            {translate('add')}
                        </Button>
                    </span>
                </Tooltip>
            }
            endButtonBar={
                <ChangeViewButtonBar
                    changeVisibleView={(view: string) =>
                        changeVisibleView(view as SupplementListViewType)
                    }
                    buttons={buttonsButtonBar}
                />
            }
        >
            <Grid
                className='flex-grow'
                columnSpacing={gridSpacing10}
                rowSpacing={gridSpacing15}
            >
                <GridChild gridProps={filterDoubleColumnGrid}>
                    <TextInput
                        labelText={'name'}
                        id={'name'}
                        value={filters.Name.value || ''}
                        handleChange={(
                            e: React.SyntheticEvent<Element, Event>,
                            newValue: string
                        ) => handleFilterChange('Name', newValue)}
                        onKeyPress={async (e: React.KeyboardEvent) => {
                            if (e.key === 'Enter') {
                                await handleSearch(1);
                            }
                        }}
                    />
                </GridChild>

                <GridChild gridProps={filterSingleColumnGrid}>
                    <LoadedOptionsTypeahead
                        labelText={'hotel'}
                        options={supplierHotels.data || []}
                        value={filters.IdSupplierHotel.value || null}
                        handleChange={(
                            e: React.SyntheticEvent<Element, Event>,
                            newValue: IFilterOptionState | null
                        ) => handleFilterChange('IdSupplierHotel', newValue)}
                        disabled={supplierHotels.isLoading}
                    />
                </GridChild>
                <GridChild gridProps={filterSingleColumnGrid}>
                    <LoadedOptionsTypeahead
                        labelText={'room'}
                        options={supplierHotelRoomTypes.data || []}
                        value={filters.IdSupplierHotelRoomType.value || null}
                        title={
                            !supplierHotelSelected
                                ? translate('choose_hotel_first')
                                : undefined
                        }
                        handleChange={(
                            e: React.SyntheticEvent<Element, Event>,
                            newValue: IFilterOptionState | null
                        ) =>
                            handleFilterChange(
                                'IdSupplierHotelRoomType',
                                newValue
                            )
                        }
                        disabled={
                            !supplierHotelSelected ||
                            supplierHotelRoomTypes.isLoading
                        }
                    />
                </GridChild>
                <GridChild gridProps={filterSingleColumnGrid}>
                    <LoadedOptionsTypeahead
                        labelText={'meal_plan'}
                        options={supplierHotelMealPlans.data || []}
                        value={filters.IdSupplierHotelMealPlan.value || null}
                        handleChange={(
                            e: React.SyntheticEvent<Element, Event>,
                            newValue: IFilterOptionState | null
                        ) =>
                            handleFilterChange(
                                'IdSupplierHotelMealPlan',
                                newValue
                            )
                        }
                        disabled={
                            !supplierHotelSelected ||
                            supplierHotelRoomTypes.isLoading
                        }
                        title={
                            !supplierHotelSelected
                                ? translate('choose_hotel_first')
                                : undefined
                        }
                    />
                </GridChild>
                <GridChild gridProps={filterSingleColumnGrid}>
                    <LoadedOptionsTypeahead
                        labelText={'rate_plan'}
                        options={supplierHotelRatePlans.data || []}
                        value={filters.IdSupplierHotelRatePlan.value || null}
                        handleChange={(
                            e: React.SyntheticEvent<Element, Event>,
                            newValue: IFilterOptionState | null
                        ) =>
                            handleFilterChange(
                                'IdSupplierHotelRatePlan',
                                newValue
                            )
                        }
                        disabled={
                            !supplierHotelSelected ||
                            supplierHotelRoomTypes.isLoading
                        }
                        title={
                            !supplierHotelSelected
                                ? translate('choose_hotel_first')
                                : undefined
                        }
                    />
                </GridChild>
                <GridChild gridProps={filterSingleColumnGrid}>
                    <DateInput
                        value={filters.FromDate.value}
                        labelText={'from'}
                        onChange={(newDate: Date | null) => {
                            handleFilterChange('FromDate', newDate);
                        }}
                    />
                </GridChild>
                <GridChild gridProps={filterSingleColumnGrid}>
                    <DateInput
                        value={filters.ToDate.value}
                        labelText={'to'}
                        onChange={(newDate: Date | null) => {
                            handleFilterChange('ToDate', newDate);
                        }}
                    />
                </GridChild>
            </Grid>
        </FiltersBar>
    );
};

export default SupplementsFiltersBar;
